import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Chip from '@mui/material/Chip'
import { useTranslation } from 'react-i18next'
import { alpha } from '@mui/material/styles'

import { PageMeta } from '../../components/common/PageMeta'
import { PhotoStrip } from '../../components/content/PhotoStrip'
import { CardSkeletonGrid, EmptyState, ErrorState, Section } from '../../components/common/States'
import { ActivityCard } from './ActivityCard'
import { contentApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { seasonColors } from '../../theme/theme'
import type { Season } from '../../types/api'

const SEASONS: Season[] = ['WINTER', 'SPRING', 'SUMMER', 'AUTUMN']
const SEASON_ICONS: Record<Season, string> = {
  WINTER: '❄️',
  SPRING: '🌱',
  SUMMER: '☀️',
  AUTUMN: '🍂',
  ALL_YEAR: '📅',
}

/**
 * The seasonal activities page.
 *
 * It opens on whatever season it actually is at the lake, not on winter or on an alphabetical
 * first tab. Somebody reading this in October wants to know about ruska and mushrooms, and
 * making them find the right tab first is a small failure that costs attention.
 */
export function ActivitiesPage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [season, setSeason] = useState<Season | null>(null)

  const currentSeason = useQuery({ queryKey: ['currentSeason'], queryFn: contentApi.currentSeason })

  useEffect(() => {
    if (currentSeason.data && season === null) {
      setSeason(currentSeason.data === 'ALL_YEAR' ? 'SUMMER' : currentSeason.data)
    }
  }, [currentSeason.data, season])

  const activeSeason = season ?? currentSeason.data ?? 'SUMMER'

  const activities = useQuery({
    queryKey: ['activities', locale, activeSeason],
    queryFn: () => contentApi.activities(locale, activeSeason),
  })

  return (
    <>
      <PageMeta title={t('activities.title')} description={t('activities.subtitle')} path="/activities" />

      <Section title={t('activities.title')} subtitle={t('activities.subtitle')}>
        <Stack spacing={4} sx={{ mb: 4 }}>
          <PhotoStrip category="ACTIVITY" count={6} />
          <PhotoStrip category="NATURE" count={6} showAllLink={false} />
          {/* The forest and mushroom films. */}
          <PhotoStrip
            category="NATURE"
            kind="VIDEO"
            count={6}
            title={t('gallery.films')}
            showAllLink={false}
          />
        </Stack>

        {currentSeason.data && (
          <Chip
            label={`${SEASON_ICONS[currentSeason.data]} ${t('activities.seasonNow', {
              season: t(`activities.${currentSeason.data}`),
            })}`}
            sx={{
              mb: 3,
              bgcolor: alpha(seasonColors[currentSeason.data], 0.14),
              color: seasonColors[currentSeason.data],
              fontWeight: 700,
            }}
          />
        )}

        <Tabs
          value={activeSeason}
          onChange={(_, value: Season) => setSeason(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 4,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': { backgroundColor: seasonColors[activeSeason], height: 3 },
          }}
        >
          {SEASONS.map((option) => (
            <Tab
              key={option}
              value={option}
              label={`${SEASON_ICONS[option]} ${t(`activities.${option}`)}`}
              sx={{
                '&.Mui-selected': { color: seasonColors[option] },
              }}
            />
          ))}
        </Tabs>

        {activities.isPending && <CardSkeletonGrid count={6} height={280} />}
        {activities.isError && (
          <ErrorState error={activities.error} onRetry={() => void activities.refetch()} />
        )}
        {activities.data?.length === 0 && (
          <EmptyState icon={SEASON_ICONS[activeSeason]} title={t('activities.empty')} />
        )}
        {activities.data && activities.data.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            }}
          >
            {activities.data.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}
          </Box>
        )}
      </Section>
    </>
  )
}
